/**
 * Load reCAPTCHA v3 script dynamically
 */
export const loadRecaptcha = () => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.grecaptcha && window.grecaptcha.ready) {
      window.grecaptcha.ready(() => resolve());
      return;
    }

    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    
    if (!siteKey) {
      console.warn('REACT_APP_RECAPTCHA_SITE_KEY not configured');
      resolve(); // Resolve anyway to not block form submission
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector(`script[src*="recaptcha"]`)) {
      // Wait for it to load
      const checkInterval = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          clearInterval(checkInterval);
          window.grecaptcha.ready(() => resolve());
        }
      }, 100);
      
      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        if (window.grecaptcha) {
          resolve();
        } else {
          reject(new Error('reCAPTCHA script failed to load'));
        }
      }, 5000);
      return;
    }

    // Create and load script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      // Wait for grecaptcha to be available
      const checkInterval = setInterval(() => {
        if (window.grecaptcha) {
          clearInterval(checkInterval);
          if (window.grecaptcha.ready) {
            window.grecaptcha.ready(() => {
              resolve();
            });
          } else {
            resolve();
          }
        }
      }, 100);
      
      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        if (window.grecaptcha) {
          resolve();
        } else {
          reject(new Error('reCAPTCHA failed to initialize'));
        }
      }, 5000);
    };
    
    script.onerror = () => {
      reject(new Error('Failed to load reCAPTCHA script'));
    };
    
    document.head.appendChild(script);
  });
};

/**
 * Get reCAPTCHA token
 * @param {string} action - Action name (e.g., 'signup', 'password_reset')
 */
export const getRecaptchaToken = async (action = 'submit') => {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  
  if (!siteKey) {
    console.warn('REACT_APP_RECAPTCHA_SITE_KEY not configured');
    return '';
  }

  try {
    // Ensure script is loaded
    await loadRecaptcha();
    
    if (!window.grecaptcha) {
      console.warn('reCAPTCHA not available');
      return '';
    }

    // Get token
    const token = await window.grecaptcha.execute(siteKey, { action });
    return token;
  } catch (error) {
    console.error('Error getting reCAPTCHA token:', error);
    return '';
  }
};
